import React, { useEffect, useMemo, useRef } from 'react';
import ArrowCircular from '@uber/base-icons/arrow-circular';
import Document from '@uber/base-icons/document';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { DURATION, useSnackbar } from 'baseui/snackbar';
import { LabelLarge, ParagraphSmall } from 'baseui/typography';
import { useAtomValue } from 'jotai';
import useShortcut from '../../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../../image/labelling/providers/utils/shortcut';
import { useOnceEffect } from '../../../modernization-common/hooks/use-once-effect';
import { useReviewMode } from '../../providers/review-mode-providers';
import { usePubSub } from '../../pub-sub';
import { batchSettingsAtom, reviewAnnotationsAtom, selectedLabelAtom } from '../atoms';
import { REVIEW_ANNOTATION_TYPE } from '../constants';
import { useReviewFeature } from '../hooks/use-review-feature';
import { useReviewDrawer } from '../review-drawer';
import { useReviewModal } from '../review-modal/use-review-modal';
import { isClassificationAnnotationId } from '../utils';
import { BatchList } from './batch-list';
import { BatchSettings } from './batch-settings';
import { BATCH_SETTINGS, EVENTS } from './constants';
import { FilterBar } from './filter-bar';
import { useGetBatchModePermissions } from './useGetBatchModePermissions';
import { getBatchItems } from './utils';
const AUTO_PLAY_MESSAGE = 'Auto-play enabled - slots will advance automatically';
export const BatchPanel = ({
  selectedId
}: {
  selectedId?: string;
}) => {
  const reviewAnnotations = useAtomValue(reviewAnnotationsAtom);
  const selectedLabels = useAtomValue(selectedLabelAtom);
  const batchSettings = useAtomValue(batchSettingsAtom);
  const [css, theme] = useStyletron();
  const {
    openFeedbackPanel
  } = useReviewDrawer();
  const {
    registerHotKeys
  } = useShortcut();
  const {
    enqueue
  } = useSnackbar();
  const permissions = useGetBatchModePermissions();
  const {
    enableContinuousPlayback
  } = useReviewFeature();
  const {
    addComment
  } = useReviewModal();
  const {
    publish
  } = usePubSub();
  const {
    toggleAnnotationVisibility
  } = useReviewMode();
  useEffect(() => {
    const shortcuts = [{
      keys: ['f'],
      onAction: (_event: KeyboardEvent) => {
        openFeedbackPanel();
      }
    }];
    if (permissions.canComment) {
      shortcuts.push({
        keys: ['Shift', 'c'],
        onAction: (event: KeyboardEvent) => {
          event.preventDefault();
          event.stopPropagation();
          addComment(selectedId);
          publish(EVENTS.COMMENT_MODAL_OPEN);
        }
      });
    }
    const {
      errors,
      removeHotKeys
    } = registerHotKeys(shortcuts);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [addComment, enableContinuousPlayback, permissions.canComment, publish, registerHotKeys, openFeedbackPanel, selectedId]);
  useOnceEffect(() => {
    if (enableContinuousPlayback) {
      enqueue({
        message: AUTO_PLAY_MESSAGE,
        startEnhancer: ({
          size
        }) => <ArrowCircular size={size} title="Continuous playback" />
      }, DURATION.short);
    }
  });
  const batchItems = useMemo(() => {
    return getBatchItems(reviewAnnotations, selectedLabels);
  }, [reviewAnnotations, selectedLabels]);
  const disabledSettingKeys = useMemo(() => isClassificationAnnotationId(selectedId) ? new Set([BATCH_SETTINGS.FOCUS_ANNOTATION]) : undefined, [selectedId]);

  // Store review annotations in a ref to avoid dependency issues
  const reviewAnnotationsRef = useRef(reviewAnnotations);
  // eslint-disable-next-line react-hooks/refs
  reviewAnnotationsRef.current = reviewAnnotations;

  // Track previous values to prevent infinite loops
  const prevFocusModeRef = useRef<boolean>();
  const prevSelectedIdRef = useRef<string | undefined>();

  // Handle focus annotation mode - only update when focus mode or selected ID actually changes
  useEffect(() => {
    if (!toggleAnnotationVisibility) {
      return;
    }
    const isFocusMode = batchSettings[BATCH_SETTINGS.FOCUS_ANNOTATION];
    const focusModeChanged = prevFocusModeRef.current !== isFocusMode;
    const selectedIdChanged = prevSelectedIdRef.current !== selectedId;

    // Only update if focus mode toggled or (focus mode is ON and selected ID changed)
    if (focusModeChanged || isFocusMode && selectedIdChanged) {
      const annotationIds = Object.keys(reviewAnnotationsRef.current).filter(annotationId => reviewAnnotationsRef.current?.[annotationId]?.type === REVIEW_ANNOTATION_TYPE.ANNOTATION && !isClassificationAnnotationId(annotationId));
      if (isFocusMode) {
        // Focus mode: show only the selected annotation
        annotationIds.forEach(annotationId => {
          const shouldHide = annotationId !== selectedId;
          toggleAnnotationVisibility(shouldHide, annotationId);
        });
      } else if (focusModeChanged) {
        // Focus mode was just turned OFF: show all annotations
        annotationIds.forEach(annotationId => {
          toggleAnnotationVisibility(false, annotationId);
        });
      }

      // Update refs
      prevFocusModeRef.current = isFocusMode;
      prevSelectedIdRef.current = selectedId;
    }
  }, [batchSettings, selectedId, toggleAnnotationVisibility]);
  return <div className={css({
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  })}>
      <div className={css({
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: theme.sizing.scale600,
      alignItems: 'center'
    })}>
        <section>
          <LabelLarge margin={0}>Slots</LabelLarge>
          <ParagraphSmall margin={0} marginBottom={theme.sizing.scale300}>
            Review and edit slots
          </ParagraphSmall>
        </section>
        {permissions.canComment && <section>
            <Button size={BUTTON_SIZE.compact} kind={BUTTON_KIND.tertiary} startEnhancer={() => <Document size={16} title="Feedback" />} onClick={openFeedbackPanel}>
              Feedback
            </Button>
          </section>}
      </div>
      <FilterBar />
      <BatchSettings disabledKeys={disabledSettingKeys} />
      <BatchList batchItems={batchItems} selectedId={selectedId} />
    </div>;
};